/**
 * Permission Context
 *
 * Provides role-based access control throughout the application.
 * Fetches granular permissions from the backend API on login and org-switch.
 * Supports feature gating, data table access, site scoping, and action permissions.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { API_BASE_URL, authFetch } from '../services/api';

// Permissions shape from the backend API
export interface UserPermissions {
  role: string;
  is_customized: boolean;
  /** Effective features: intersection of user role and org entitlement. */
  allowed_features: string[];
  /** Raw plan features the org has paid for. Used to render padlock states
   *  for sections the org isn't entitled to. */
  organisation_entitled_features: string[];
  allowed_data_tables: string[];
  allowed_site_ids: number[];
  can_send_supplier_emails: boolean;
  can_export_reports: boolean;
  can_manage_users: boolean;
  can_delete_data: boolean;
  can_edit_dashboards: boolean;
  can_delete_dashboards: boolean;
  can_manage_organisation: boolean;
  is_org_admin: boolean;
  is_system_admin: boolean;
  /** True when this user has a PENDING ``OrganisationClaim`` against the
   *  current organisation. Used to render the "Limited access" banner on the
   *  ESG dashboard regardless of the effective role (a system-admin tester
   *  with `is_staff` would otherwise be treated as `admin` and hide the
   *  banner even though their claim is genuinely awaiting verification). */
  has_pending_claim: boolean;
  /** GHG inventory category codes the org has marked Not Relevant or
   *  Excluded. Used to suppress nav pages whose content is no longer
   *  applicable (e.g. /investments hides when scope_3_15 is excluded). */
  inventory_excluded_categories: string[];
}

// Empty permissions (no access)
const EMPTY_PERMISSIONS: UserPermissions = {
  role: '',
  is_customized: false,
  allowed_features: [],
  organisation_entitled_features: [],
  allowed_data_tables: [],
  allowed_site_ids: [],
  can_send_supplier_emails: false,
  can_export_reports: false,
  can_manage_users: false,
  can_delete_data: false,
  can_edit_dashboards: false,
  can_delete_dashboards: false,
  can_manage_organisation: false,
  is_org_admin: false,
  is_system_admin: false,
  has_pending_claim: false,
  inventory_excluded_categories: [],
};

// Legacy Permission type for backwards compatibility
export type Permission =
  | 'view:esg_assessment'
  | 'edit:esg_assessment'
  | 'submit:esg_assessment'
  | 'view:products'
  | 'edit:products'
  | 'delete:products'
  | 'view:suppliers'
  | 'edit:suppliers'
  | 'view:emissions'
  | 'edit:emissions'
  | 'view:reports'
  | 'export:reports'
  | 'view:audit_trail'
  | 'manage:organisation'
  | 'manage:users'
  | 'manage:data_sharing'
  | 'view:shared_data'
  | 'admin:system';

// Permission context interface
interface PermissionContextType {
  // Current permissions from API
  userPermissions: UserPermissions;

  // Loading state
  isLoading: boolean;

  // Current user's role in the selected organisation
  currentRole: string | null;

  // Feature access checks (effective: user role ∩ org entitlement)
  hasFeature: (feature: string) => boolean;

  // Org-level entitlement check — does the org's plan include this feature?
  // True even if the current user's role doesn't grant it. Used to render
  // padlock states in the navbar for sections the org hasn't paid for.
  isFeatureEntitled: (feature: string) => boolean;

  // Data table access checks
  hasDataTable: (table: string) => boolean;

  // Site access check (empty allowed_site_ids = all sites)
  canAccessSite: (siteId: number) => boolean;

  // Action permission checks
  hasAction: (action: string) => boolean;

  // Legacy permission check (maps to features/actions)
  hasPermission: (permission: Permission) => boolean;
  hasAnyPermission: (permissions: Permission[]) => boolean;
  hasAllPermissions: (permissions: Permission[]) => boolean;

  // Check if user is system admin
  isSystemAdmin: boolean;

  // Check if user is organisation admin
  isOrgAdmin: boolean;

  // Legacy convenience methods
  canView: (feature: string) => boolean;
  canEdit: (feature: string) => boolean;

  // Get all permissions for current context
  permissions: Permission[];

  // Refresh permissions from API
  refreshPermissions: () => Promise<void>;
}

// Create context
const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

/**
 * Map a feature key to legacy Permission strings
 */
function featureToLegacyPermissions(feature: string): Permission[] {
  const map: Record<string, Permission[]> = {
    esg_assessment: ['view:esg_assessment', 'edit:esg_assessment', 'submit:esg_assessment'],
    products: ['view:products', 'edit:products', 'delete:products'],
    suppliers: ['view:suppliers', 'edit:suppliers'],
    supplier_engagement: ['view:suppliers', 'edit:suppliers'],
    emissions_calculations: ['view:emissions', 'edit:emissions'],
    reporting: ['view:reports', 'export:reports'],
  };
  return map[feature] || [];
}

/**
 * Permission Provider Component
 */
export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, selectedOrganisationId, isAuthenticated } = useAuth();
  const [userPermissions, setUserPermissions] = useState<UserPermissions>(EMPTY_PERMISSIONS);
  // Always start loading=true. On a hard refresh, isAuthenticated is briefly
  // false while AuthContext loads — initialising from that value here used
  // to leave permissions thinking they're "ready" with EMPTY_PERMISSIONS,
  // so ProtectedRoute would redirect to DEFAULT_PATH on the very first
  // post-auth render before the permission fetch had a chance to fire.
  // Setting true at mount blocks that race; the effect below resolves it
  // promptly for both authed and unauthed paths.
  const [isLoading, setIsLoading] = useState(true);
  // #18: the org id the CURRENT userPermissions were fetched for. On a hard
  // refresh of a deep route, auth resolves (isAuthenticated flips true) one
  // render BEFORE the effect below runs fetchPermissions + setIsLoading(true).
  // In that gap isLoading is still false (from the unauthenticated render) and
  // permissions are EMPTY, so a route guard would wrongly redirect to the
  // default page. Tracking which org the permissions belong to lets us report
  // "still loading" until they match the selected org.
  const [permissionsOrgId, setPermissionsOrgId] = useState<number | null>(null);

  /**
   * Fetch permissions from the backend API
   */
  const fetchPermissions = useCallback(async (orgId: number, userId: number) => {
    setIsLoading(true);
    try {
      const response = await authFetch(
        `${API_BASE_URL}/api/organisations/my-permissions/?organisation_id=${orgId}&user_id=${userId}`
      );
      if (response.ok) {
        const data = await response.json();
        setUserPermissions(data);
      } else {
        console.error('Failed to fetch permissions:', response.status);
        setUserPermissions(EMPTY_PERMISSIONS);
      }
    } catch (err) {
      console.error('Error fetching permissions:', err);
      setUserPermissions(EMPTY_PERMISSIONS);
    } finally {
      // Record which org these permissions belong to, so a guard can tell a
      // genuine "no access" from "not fetched yet" (#18).
      setPermissionsOrgId(orgId);
      setIsLoading(false);
    }
  }, []);

  /**
   * Refresh permissions (can be called externally)
   */
  const refreshPermissions = useCallback(async () => {
    if (user && selectedOrganisationId) {
      await fetchPermissions(selectedOrganisationId, user.id);
    }
  }, [user, selectedOrganisationId, fetchPermissions]);

  /**
   * Fetch permissions when user or org changes
   */
  useEffect(() => {
    if (isAuthenticated && user && selectedOrganisationId) {
      fetchPermissions(selectedOrganisationId, user.id);
    } else if (isAuthenticated && user && !selectedOrganisationId) {
      // Authenticated but no org selected (e.g. registration where
      // organisation assignment hasn't fully propagated). Stop loading
      // so the UI doesn't hang; user will see limited features until
      // an org is selected or the page is refreshed.
      setUserPermissions(EMPTY_PERMISSIONS);
      setIsLoading(false);
    } else if (isAuthenticated) {
      // Authenticated but user data still hydrating — keep loading briefly
      setIsLoading(true);
    } else {
      // Not authenticated — no permissions needed
      setUserPermissions(EMPTY_PERMISSIONS);
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.id, selectedOrganisationId, fetchPermissions]);

  // Feature access (effective: already gated by org entitlements server-side)
  const hasFeature = (feature: string): boolean => {
    return userPermissions.allowed_features.includes(feature);
  };

  // Org-level entitlement check. System admins bypass org entitlements so
  // they can always access every feature for support/debugging; the backend
  // already returns ALL_FEATURES for them in this list.
  const isFeatureEntitled = (feature: string): boolean => {
    if (userPermissions.is_system_admin) return true;
    return userPermissions.organisation_entitled_features.includes(feature);
  };

  // Data table access
  const hasDataTable = (table: string): boolean => {
    return userPermissions.allowed_data_tables.includes(table);
  };

  // Site access (empty = all sites)
  const canAccessSite = (siteId: number): boolean => {
    if (userPermissions.allowed_site_ids.length === 0) return true;
    return userPermissions.allowed_site_ids.includes(siteId);
  };

  // Action access
  const hasAction = (action: string): boolean => {
    return (userPermissions as unknown as Record<string, unknown>)[action] === true;
  };

  // Compute legacy permissions from the new system
  const legacyPermissions: Permission[] = (() => {
    if (!isAuthenticated) return [];
    if (userPermissions.is_system_admin) {
      return [
        'view:esg_assessment', 'edit:esg_assessment', 'submit:esg_assessment',
        'view:products', 'edit:products', 'delete:products',
        'view:suppliers', 'edit:suppliers',
        'view:emissions', 'edit:emissions',
        'view:reports', 'export:reports',
        'view:audit_trail',
        'manage:organisation', 'manage:users', 'manage:data_sharing',
        'view:shared_data', 'admin:system',
      ];
    }

    const perms: Permission[] = ['view:shared_data'];
    for (const feature of userPermissions.allowed_features) {
      perms.push(...featureToLegacyPermissions(feature));
    }
    if (userPermissions.can_export_reports) perms.push('export:reports');
    if (userPermissions.can_manage_users) perms.push('manage:users');
    if (userPermissions.can_manage_organisation) {
      perms.push('manage:organisation', 'manage:data_sharing');
    }
    if (userPermissions.can_delete_data) perms.push('delete:products');
    if (userPermissions.is_org_admin) perms.push('view:audit_trail');

    return Array.from(new Set(perms));
  })();

  // Legacy permission checks
  const hasPermission = (permission: Permission): boolean => {
    return legacyPermissions.includes(permission);
  };

  const hasAnyPermission = (permissionList: Permission[]): boolean => {
    return permissionList.some((p) => legacyPermissions.includes(p));
  };

  const hasAllPermissions = (permissionList: Permission[]): boolean => {
    return permissionList.every((p) => legacyPermissions.includes(p));
  };

  const canView = (feature: string): boolean => {
    const permission = `view:${feature}` as Permission;
    return hasPermission(permission);
  };

  const canEdit = (feature: string): boolean => {
    const permission = `edit:${feature}` as Permission;
    return hasPermission(permission);
  };

  // #18: also report "loading" during the post-auth gap where an org is
  // selected but its permissions haven't been fetched yet (permissionsOrgId
  // still lags selectedOrganisationId). This stops route guards redirecting on
  // the empty permissions that exist for that one render.
  const permissionsPending =
    isLoading ||
    (isAuthenticated &&
      selectedOrganisationId != null &&
      permissionsOrgId !== selectedOrganisationId);

  const contextValue: PermissionContextType = {
    userPermissions,
    isLoading: permissionsPending,
    currentRole: userPermissions.role || null,
    hasFeature,
    isFeatureEntitled,
    hasDataTable,
    canAccessSite,
    hasAction,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isSystemAdmin: userPermissions.is_system_admin,
    isOrgAdmin: userPermissions.is_org_admin,
    canView,
    canEdit,
    permissions: legacyPermissions,
    refreshPermissions,
  };

  return (
    <PermissionContext.Provider value={contextValue}>
      {children}
    </PermissionContext.Provider>
  );
};

/**
 * Hook to use permission context
 */
export function usePermissions(): PermissionContextType {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
}

/**
 * Higher-order component for permission-based rendering
 */
interface RequirePermissionProps {
  permission: Permission | Permission[];
  fallback?: React.ReactNode;
  children: React.ReactNode;
}

export const RequirePermission: React.FC<RequirePermissionProps> = ({
  permission,
  fallback = null,
  children,
}) => {
  const { hasPermission, hasAnyPermission } = usePermissions();

  const hasAccess = Array.isArray(permission)
    ? hasAnyPermission(permission)
    : hasPermission(permission);

  if (!hasAccess) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

/**
 * Component that shows content only for org admins
 */
export const RequireOrgAdmin: React.FC<{ children: React.ReactNode; fallback?: React.ReactNode }> = ({
  children,
  fallback = null,
}) => {
  const { isOrgAdmin } = usePermissions();

  if (!isOrgAdmin) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

/**
 * Component that shows content only for system admins
 */
export const RequireSystemAdmin: React.FC<{ children: React.ReactNode; fallback?: React.ReactNode }> = ({
  children,
  fallback = null,
}) => {
  const { isSystemAdmin } = usePermissions();

  if (!isSystemAdmin) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

/**
 * Component that shows content only if user has a specific feature
 */
export const RequireFeature: React.FC<{ feature: string; children: React.ReactNode; fallback?: React.ReactNode }> = ({
  feature,
  children,
  fallback = null,
}) => {
  const { hasFeature } = usePermissions();

  if (!hasFeature(feature)) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

export default PermissionContext;
